import type { ColorVariable, FontFamilyVariable, SizeVariable } from "@/store/variablesStore";

export interface CSSVariable {
  name: string;
  value: string;
  type: "color" | "size" | "fontFamily";
}

/**
 * Converts a variable name to a valid CSS custom property name
 * e.g., "primary-blue" -> "--primary-blue"
 */
export const toCSSVariableName = (name: string): string => {
  // Convert to kebab-case and add -- prefix
  return `--${name.toLowerCase().replace(/[^a-z0-9-]/g, "-")}`;
};

/**
 * Converts a variable name to a CSS var() reference
 * e.g., "primary-blue" -> "var(--primary-blue)"
 */
export const toCSSVarReference = (name: string): string => {
  return `var(${toCSSVariableName(name)})`;
};

/**
 * Generates CSS custom properties from project variables
 */
export const generateCSSVariables = (
  colors: ColorVariable[],
  sizes: SizeVariable[],
  fontFamilies: FontFamilyVariable[],
): string => {
  const cssVars: string[] = [];
  const fontFaces: string[] = [];

  // Add color variables
  colors.forEach((color) => {
    const varName = toCSSVariableName(color.name);
    cssVars.push(`  ${varName}: ${color.value};`);
  });

  // Add size variables
  sizes.forEach((size) => {
    const varName = toCSSVariableName(size.name);
    cssVars.push(`  ${varName}: ${size.value};`);
  });

  // Add font family variables
  fontFamilies.forEach((font) => {
    const varName = toCSSVariableName(font.name);
    cssVars.push(`  ${varName}: ${font.value};`);
    if (font.src) {
      const family =
        font.value
          .split(",")[0]
          ?.trim()
          .replace(/^["']|["']$/g, "") || font.name;
      const format = font.format || "woff2";
      fontFaces.push(
        `@font-face { font-family: "${family}"; src: url("${font.src}") format("${format}"); font-weight: normal; font-style: normal; font-display: swap; }`,
      );
    }
  });

  const rootVars = cssVars.length > 0 ? `:root {\n${cssVars.join("\n")}\n}` : "";
  if (fontFaces.length === 0) return rootVars;
  return `${fontFaces.join("\n")}${rootVars ? `\n${rootVars}` : ""}`;
};

/**
 * Injects CSS variables into the document
 */
export const injectCSSVariables = (cssVariables: string, projectId: string) => {
  // Remove existing style element for this project
  const existingStyle = document.getElementById(`css-variables-${projectId}`);
  if (existingStyle) {
    existingStyle.remove();
  }

  // Create new style element
  const style = document.createElement("style");
  style.id = `css-variables-${projectId}`;
  style.textContent = cssVariables;
  document.head.appendChild(style);
};

/**
 * Removes CSS variables for a project
 */
export const removeCSSVariables = (projectId: string) => {
  const existingStyle = document.getElementById(`css-variables-${projectId}`);
  if (existingStyle) {
    existingStyle.remove();
  }
};

/**
 * Gets all available CSS variable names for a project
 */
export const getAvailableCSSVariables = (
  colors: ColorVariable[],
  sizes: SizeVariable[],
  fontFamilies: FontFamilyVariable[],
): CSSVariable[] => {
  const variables: CSSVariable[] = [];

  colors.forEach((color) => {
    variables.push({
      name: toCSSVariableName(color.name),
      value: color.value,
      type: "color" as const,
    });
  });

  sizes.forEach((size) => {
    variables.push({
      name: toCSSVariableName(size.name),
      value: size.value,
      type: "size" as const,
    });
  });

  fontFamilies.forEach((font) => {
    variables.push({
      name: toCSSVariableName(font.name),
      value: font.value,
      type: "fontFamily" as const,
    });
  });

  return variables;
};
